/**
 * Domain types.
 *
 * The status strings are the exact values of the data-validation dropdown on
 * the TASKS tab, read from the live sheet on 2026-09-17. Title case with a
 * capital second word. Do not "tidy" these: a value that does not match the
 * validation rule exactly is either rejected by the sheet or silently creates
 * a phantom status that no filter will ever match.
 */
export const STATUSES = ["Not Started", "In Progress", "Stuck", "Done"] as const;
export type Status = (typeof STATUSES)[number];

export function isStatus(value: string): value is Status {
  return (STATUSES as readonly string[]).includes(value);
}

export interface Task {
  /** Stable identifier stored in column L. Never derived from row position. */
  id: string;
  /** Stable id of the owning project, stored in column P. */
  projectId: string;
  /** Project name as it appears in column B. Kept in sync on rename. */
  projectName: string;
  title: string;
  /** Free text, e.g. "Pilot testing". Pre-existing column, still edited. */
  activity: string;
  /** WP-NN id matching the project mindmap. New column. */
  workPackage: string;
  cooperateWith: string;
  /** ISO date string (YYYY-MM-DD) or "". */
  deadline: string;
  /** ISO date string (YYYY-MM-DD) or "". Column H, set when a task is done. */
  dateDone: string;
  notes: string;
  status: Status;
  resultLink: string;
  /** One-line reason recorded when a task is done with no deliverable. */
  outcome: string;
  /** Reserved for calendar sync. Mapped but unused today. */
  calendarEventId: string;
  deleted: boolean;
  /** Physical sheet row. Cache only, always re-resolved from id before writing. */
  sheetRow: number;
}

export type TaskDraft = Omit<
  Task,
  "id" | "sheetRow" | "deleted" | "calendarEventId"
>;

export interface Project {
  id: string;
  name: string;
  /** Hex colour, e.g. "#1D9E75". */
  color: string;
  archived: boolean;
  /** Filename under public/mindmaps/, or "". */
  mindmap: string;
  /** Absolute https link to the project's task file, or "". */
  taskFile: string;
  /** Absolute https link to the project's Drive folder, or "". */
  driveFolder: string;
  sheetRow: number;
}

export type ProjectDraft = Omit<Project, "id" | "sheetRow">;
